import * as XLSX from 'xlsx';
import type { Transaction } from '@/types/Transaction';
import { MONTH_NAMES_PT, formatMonthKey, normalizeDateCell, parseMonthKey } from './dates';

const MONTH_HEADER_RE = /^([A-Za-zÀ-ÿ]+)\s+de\s+(\d{4})$/;
const TOTAL_RE = /^TOTAL(\s+GLOBAL)?\s*$/i;

const MONTH_INDEX = new Map<string, number>(
  MONTH_NAMES_PT.flatMap((n, i) => [
    [n.toLowerCase(), i],
    [n.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''), i],
  ]),
);

export interface ParserDiagnostics {
  /** Nº de blocos mensais identificados (esperado: 13). */
  blocksFound: number;
  /** Linhas saltadas (cabeçalhos, vazias, TOTAL, etc.). */
  rowsSkipped: number;
  /** Linhas de dados processadas com sucesso. */
  rowsProcessed: number;
  /** Erros encontrados durante o parse. */
  errors: { rowIndex: number; message: string }[];
  /** Contagem de transacções por monthKey. */
  countByMonth: Record<string, number>;
}

export interface ParseResult {
  transactions: Transaction[];
  diagnostics: ParserDiagnostics;
}

/**
 * Estado da máquina de estados que percorre as linhas da sheet.
 * - 'looking-for-month': procura header tipo "Março de 2026".
 * - 'expecting-column-header': consumiu header de mês, próxima linha pode ser
 *   "Data" (header de colunas) ou já dados (caso o header tenha sido omitido).
 * - 'reading-data': processa linhas até encontrar TOTAL.
 */
type ParserState = 'looking-for-month' | 'expecting-column-header' | 'reading-data';

/**
 * Parser principal de Mensalidades. Recebe um workbook XLSX e devolve as
 * transacções normalizadas + diagnósticos. Tolerante a:
 *  - sufixos como " ⓘ" em cabeçalhos de coluna (D/E/F)
 *  - col A em formatos string "DD/MM" / Date nativo / vazio (herda)
 *  - blocos sem header de colunas repetido (heurística fallback)
 *  - múltiplos espaços, casing variável em "TOTAL"
 *
 * Erros não fatais são acumulados em diagnostics.errors — o parse continua.
 */
export function parseMensalidades(workbook: XLSX.WorkBook): ParseResult {
  const sheet = workbook.Sheets['Mensalidades'];
  if (!sheet) {
    throw new Error('Sheet "Mensalidades" não encontrada no ficheiro .xlsx.');
  }

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
  });

  const transactions: Transaction[] = [];
  const diagnostics: ParserDiagnostics = {
    blocksFound: 0,
    rowsSkipped: 0,
    rowsProcessed: 0,
    errors: [],
    countByMonth: {},
  };

  let state: ParserState = 'looking-for-month';
  let currentYear = 0;
  let currentMonth = 0;
  let currentMonthKey = '';
  let previousDate: Date | null = null;
  let indexInBlock = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] ?? [];
    const rowIndex = i + 1; // 1-based para diagnósticos legíveis vs Excel
    const cellA = row[0];

    // Identificação de header de mês — pode acontecer em qualquer state
    // (defensivo: se o parser perdeu sync, recupera ao encontrar próximo header).
    if (typeof cellA === 'string') {
      const monthMatch = cellA.trim().match(MONTH_HEADER_RE);
      if (monthMatch) {
        const monthName = monthMatch[1];
        const yearStr = monthMatch[2];
        if (!monthName || !yearStr) {
          diagnostics.rowsSkipped++;
          continue;
        }
        const monthIdx = MONTH_INDEX.get(monthName.toLowerCase());
        if (monthIdx === undefined) {
          diagnostics.errors.push({
            rowIndex,
            message: `Header de mês com nome desconhecido: "${cellA}"`,
          });
          continue;
        }
        currentYear = Number(yearStr);
        currentMonth = monthIdx;
        currentMonthKey = formatMonthKey(currentYear, currentMonth);
        previousDate = null;
        indexInBlock = 0;
        diagnostics.blocksFound++;
        state = 'expecting-column-header';
        diagnostics.rowsSkipped++; // o próprio header
        continue;
      }
    }

    // TOTAL termina o bloco actual
    if (typeof cellA === 'string' && TOTAL_RE.test(cellA)) {
      state = 'looking-for-month';
      diagnostics.rowsSkipped++;
      continue;
    }

    // Skip linha vazia ou linha de título global ("REGISTOS DE PAGAMENTOS...")
    if (state === 'looking-for-month') {
      diagnostics.rowsSkipped++;
      continue;
    }

    // Header de colunas repetido ("Data" em A)
    if (state === 'expecting-column-header') {
      if (typeof cellA === 'string' && cellA.trim().toLowerCase() === 'data') {
        state = 'reading-data';
        diagnostics.rowsSkipped++;
        continue;
      }
      // Header foi omitido — se a linha já parece dados (tem fornecedor em C),
      // avança directo para leitura, processando esta linha.
      if (row[2]) {
        state = 'reading-data';
        // fall-through para processar esta linha
      } else {
        diagnostics.rowsSkipped++;
        continue;
      }
    }

    // state === 'reading-data'
    const supplier = row[2];
    if (typeof supplier !== 'string' || !supplier.trim()) {
      diagnostics.rowsSkipped++;
      continue;
    }

    let date = normalizeDateCell(cellA, currentYear, previousDate);
    if (!date) {
      diagnostics.errors.push({
        rowIndex,
        message: `Data inválida ou em falta na linha ${rowIndex} (col A="${String(cellA)}")`,
      });
      continue;
    }
    // Defensivo: o Excel guarda algumas celulas como Date object com ano/mes
    // errado (resultado de edicoes "DD/MM" promovidas a serial date com ano
    // 1900 ou current). Forcar ano/mes do bloco actual quando houver mismatch.
    if (date.getFullYear() !== currentYear || date.getMonth() !== currentMonth) {
      date = new Date(Date.UTC(currentYear, currentMonth, date.getUTCDate()));
    }
    previousDate = date;
    indexInBlock++;

    const service = typeof row[1] === 'string' ? row[1].trim() : String(row[1] ?? '');
    const category = typeof row[8] === 'string' ? row[8].trim() : '';
    const gross = numberOrZero(row[3], rowIndex, 'D', diagnostics);
    const net = numberOrZero(row[4], rowIndex, 'E', diagnostics);
    const refund = numberOrZero(row[5], rowIndex, 'F', diagnostics);

    // Mês_Chave (col G) pode estar preenchido ou em falta — fallback para o derivado.
    let monthKey = currentMonthKey;
    if (typeof row[6] === 'string' && row[6].trim()) {
      const parsedKey = parseMonthKey(row[6]);
      if (parsedKey && (parsedKey.year !== currentYear || parsedKey.month !== currentMonth)) {
        diagnostics.errors.push({
          rowIndex,
          message: `Mês_Chave "${row[6]}" diverge do bloco "${currentMonthKey}"`,
        });
      }
      monthKey = row[6].trim();
    }

    transactions.push({
      id: `${currentMonthKey}#${indexInBlock}#${supplier.trim()}`,
      date,
      service,
      supplier: supplier.trim(),
      category,
      gross,
      net,
      refund,
      monthKey,
      rowIndex,
    });

    diagnostics.rowsProcessed++;
    diagnostics.countByMonth[currentMonthKey] = (diagnostics.countByMonth[currentMonthKey] ?? 0) + 1;
  }

  return { transactions, diagnostics };
}

function numberOrZero(
  value: unknown,
  rowIndex: number,
  col: string,
  diagnostics: ParserDiagnostics,
): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value == null || value === '') return 0;
  if (typeof value === 'string') {
    // Tolerar strings formatadas ("69,36 €") como defensivo.
    const cleaned = value.replace(/€/g, '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
    const n = Number(cleaned);
    if (Number.isFinite(n)) return n;
  }
  diagnostics.errors.push({
    rowIndex,
    message: `Valor numérico inválido na coluna ${col}: ${JSON.stringify(value)}`,
  });
  return 0;
}
